import { afterNextRender, Component, ElementRef, inject, input, Renderer2, signal, viewChildren } from '@angular/core';
import { ResourceType } from 'src/app/models/api/common';
import { ResourceCategory } from 'src/app/models/api/inventoryResource.model';
import { CardFieldKey, ICardField } from 'src/app/models/viewModels/cardModel';
import { mcpKeysConversionMap } from 'src/app/models/constants/mcpKeysNaming.constants';
import { GetSensitiveDataActionComponent } from '../get-sensitive-data-input/get-sensitive-data-input.component';
import { CommonModule } from '@angular/common';
import { AdditionalValuesComponent } from '../additional-values/additional-values.component';
import { ToggleContentComponent } from '../toggle-content/toggle-content.component';
import { ToNumberedListPipe } from '../../pipes/toNumberedList/to-numbered-list.pipe';
import { NewLineCharToHtmlLineBreak } from '../../pipes/newLineCharToHtmlLineBreak/new-line-char-to-html-line-break.pipe';
import { LabelsProviderPipe } from '../../pipes/labels-provider/labels-provider.pipe';
import { BeautifyMcpNamingPipe } from '../../pipes/beautify-mcp-naming/beautify-mcp-naming.pipe';
import { CopyButtonComponent } from '../copy-button/copy-button.component';
import { RiskMappingScoreColumnComponent } from '../risk-mapping-score-column/risk-mapping-score-column.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AnchorLinkComponent } from '../anchor-button/anchor-link.component';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import { InternalNavigationTriggerModel } from 'src/app/models/navigation/internalNavigationTrigger.model';

@Component({
  selector: 'app-card-fields',
  standalone: true,
  templateUrl: './card-fields.component.html',
  styleUrls: ['./card-fields.component.scss'],
  imports: [
    CommonModule,
    ToggleContentComponent,
    GetSensitiveDataActionComponent,
    AdditionalValuesComponent,
    ToNumberedListPipe,
    NewLineCharToHtmlLineBreak,
    LabelsProviderPipe,
    BeautifyMcpNamingPipe,
    CopyButtonComponent,
    RiskMappingScoreColumnComponent,
    MatTooltipModule,
    AnchorLinkComponent,
  ],
})
export class CardFieldsComponent {
  public cardFields = input.required<ICardField[]>();
  public verticalOrder = input<boolean>(false);
  public resourceType = input<ResourceType>();
  public resourceCategory = input<ResourceCategory>();
  public isToggleContentAllowed = input<boolean>(false);
  public overflowHorizontalItems = signal<boolean[]>([]);
  public overflowVerticalConversationStartersBlock = signal<boolean>(false);

  public resourceTypeEnum = ResourceType;
  public resourceCategoryEnum = ResourceCategory;
  public readonly conversationStartersFieldName = CardFieldKey.ConversationStarters;
  public readonly resourceDescriptionFieldName = CardFieldKey.ResourceDescription;
  public readonly instructionsFieldName = CardFieldKey.Instructions;
  public readonly webBrowsingCapabilityFieldName = CardFieldKey.WebBrowsingCapability;

  private readonly _gptAdditionalInfoFieldsWithCopyButton = [
    'Access',
    'Categories',
    'Conversation Starters',
    'Dalle Capability',
    'Canvas Capability',
    'Code Interpreter Capability',
    'GPT chat URL',
  ];
  private readonly _gptOtherFieldsWithCopyButton = ['Instructions', 'Web Browsing Capability'];
  public readonly copyableFields = [
    ...this._gptAdditionalInfoFieldsWithCopyButton,
    ...this._gptOtherFieldsWithCopyButton,
  ];

  public readonly truncatedMaxHeightForConversationStartersBlock = 60;
  public readonly mcpKeysConversionMap = mcpKeysConversionMap;

  private readonly _elements = viewChildren<ElementRef<HTMLElement>>('fieldEl');

  private readonly _renderer: Renderer2 = inject(Renderer2);
  private readonly _sidePanelService = inject(SidePanelService);

  public shouldUseClickHandler(navigation: InternalNavigationTriggerModel): boolean {
    // Use click handler for stacking unless explicitly excluded
    return !navigation.excludeFromStacking;
  }

  public onInternalNavigationClick(event: MouseEvent, navigation: InternalNavigationTriggerModel): void {
    event.preventDefault();
    event.stopPropagation();
    // Auto-stacking: navigateStacked will stack if panel is open
    this._sidePanelService.navigateStacked(navigation.query);
  }

  constructor() {
    afterNextRender(() => {
      this.overflowHorizontalItems.set([]);
      this.overflowVerticalConversationStartersBlock.set(false);

      const elements = this._elements();

      this._setOverflowHorizontalItems(elements);
      this._setOverflowVerticalConversationStartersBlock(elements);
    });
  }

  public isDictSeverityNotEmpty(dictSeverity: Record<string, number> | undefined): boolean {
    if (dictSeverity && Object.keys(dictSeverity).length > 0) {
      return true;
    } else {
      return false;
    }
  }

  public getClipboardText(value: string, additionalValues: string[] | undefined): string {
    return additionalValues?.length ? `${value}, ${additionalValues.join(', ')}` : value;
  }

  public shouldRenderAsHtml(cardField: ICardField): boolean {
    return (
      this.resourceType() === this.resourceTypeEnum.gpt &&
      cardField.key === this.conversationStartersFieldName &&
      !this.overflowVerticalConversationStartersBlock()
    );
  }

  private _checkHorizontalOverflow(element: HTMLElement): boolean {
    const clone = this._createElementClone(element);

    // Width measurement couldn't be done without adding element clone to the DOM
    this._renderer.appendChild(document.body, clone);

    const fullWidth = clone.scrollWidth;

    const paddingOverlap = 20; // Adjust for paddings and margins
    const visibleWidth = element.offsetWidth - paddingOverlap;

    this._renderer.removeChild(document.body, clone);

    return fullWidth > visibleWidth;
  }

  // Makes copy of the element to safely measure the full text width of content
  // Removes width constraints and hides the clone off-screen
  private _createElementClone(element: HTMLElement): HTMLElement {
    const clone = element.cloneNode(true) as HTMLElement;

    this._renderer.setStyle(clone, 'max-width', 'none');
    this._renderer.setStyle(clone, 'width', 'auto');

    this._renderer.setStyle(clone, 'white-space', 'nowrap');
    this._renderer.setStyle(clone, 'position', 'absolute');
    this._renderer.setStyle(clone, 'visibility', 'hidden');

    return clone;
  }

  private _setOverflowHorizontalItems(elements: readonly ElementRef<HTMLElement>[]): void {
    const flags = elements.map((elRef) => this._checkHorizontalOverflow(elRef.nativeElement));
    this.overflowHorizontalItems.set(flags);
  }

  private _setOverflowVerticalConversationStartersBlock(elements: readonly ElementRef<HTMLElement>[]): void {
    const idx = this.cardFields().findIndex((f) => f.key === this.conversationStartersFieldName);
    if (idx >= 0) {
      const el = elements[idx].nativeElement;
      this.overflowVerticalConversationStartersBlock.set(
        el.scrollHeight > this.truncatedMaxHeightForConversationStartersBlock
      );
    } else {
      this.overflowVerticalConversationStartersBlock.set(false);
    }
  }
}
